/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
import React, { useState } from "react";
import { Box, makeStyles, Switch, Tooltip } from "@material-ui/core";
import { useSelector } from "react-redux";
import LoadingSpinner from "app/auth/utils/LoadingSpinnerComponent";
import ScenarioMatrixCell from "./ScenarioMatrixCell";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    overflowY: "auto",
  },
  columnHeader: {
    display: "flex",
    alignItems: "end",
    gap: "5px",
    maxHeight: "40px",
  },
  emptySquare: {
    backgroundColor: "#fff",
    height: "35px",
    width: "40px",
    minWidth: "40px",
    borderRadius: 4,
  },
  columnHeaderCell: {
    boxSizing: "border-box",
    flex: "1 1 auto",
    backgroundColor: "#e86861",
    maxHeight: "35px",
    minWidth: "130px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    textAlign: "center",
    color: "#fff",
    padding: "8px",
    cursor: "default",
    [theme.breakpoints.up(2560)]: { fontSize: "19px" },
    [theme.breakpoints.between(1920, 2560)]: { fontSize: "17px" },
    [theme.breakpoints.between(1440, 1920)]: { fontSize: "15px" },
    [theme.breakpoints.between(1280, 1440)]: { fontSize: "14px" },
    [theme.breakpoints.down(1280)]: { fontSize: "14px" },
  },
  rowHeader: {
    display: "grid",
    height: "100%",
    gridTemplateColumns: "40px repeat(5, 1fr)",
    gridTemplateRows: "repeat(5, 1fr)",
    gap: "5px",
  },
  rowHeaderCell: {
    flex: "1 1 auto",
    backgroundColor: "#181E2A",
    width: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    textAlign: "center",
    color: "#fff",
    cursor: "default",
    [theme.breakpoints.up(2560)]: { fontSize: "19px" },
    [theme.breakpoints.between(1920, 2560)]: { fontSize: "17px" },
    [theme.breakpoints.between(1440, 1920)]: { fontSize: "15px" },
    [theme.breakpoints.between(1280, 1440)]: { fontSize: "14px" },
    [theme.breakpoints.down(1280)]: { fontSize: "14px" },
  },
  spinnerContainer: {
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  toggleContainer: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "end",
  },
  tooltipToggleInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    fontSize: "inherit",
    fontFamily: "inherit",
    cursor: "default",
    padding: "10px 10px",
    [theme.breakpoints.up(2560)]: { fontSize: "16px" },
    [theme.breakpoints.between(1920, 2560)]: { fontSize: "14.5px" },
    [theme.breakpoints.between(1440, 1920)]: { fontSize: "13px" },
    [theme.breakpoints.between(1280, 1440)]: { fontSize: "12px" },
    [theme.breakpoints.down(1280)]: { fontSize: "12px" },
  },
  headerTooltip: {
    padding: "10px 10px",
    [theme.breakpoints.up(2560)]: { fontSize: "16px" },
    [theme.breakpoints.between(1920, 2560)]: { fontSize: "14.5px" },
    [theme.breakpoints.between(1440, 1920)]: { fontSize: "13px" },
    [theme.breakpoints.between(1280, 1440)]: { fontSize: "12px" },
    [theme.breakpoints.down(1280)]: { fontSize: "12px" },
  },
}));

const ScenarioMatrix = ({ selectedScenariosData }) => {
  const classes = useStyles();
  const { totalsByScenarioId } = useSelector((state) => state.scenarios);
  const { selectedScenarios, fetchingDataForScenarios } = useSelector(
    (state) => state.summary
  );
  const [scenarioBaseDirection, setScenarioBaseDirection] = useState("row");

  const generateMatrixCells = () => {
    return Array(5)
      .fill(null)
      .map((_, rowIndex) => ({
        scenarioName: `Sc. ${rowIndex + 1}`,
        cells: Array(5)
          .fill(null)
          .map((__, colIndex) => ({
            scenarioBaseId: selectedScenarios[rowIndex],
            scenarioToCompareId: selectedScenarios[colIndex],
            isDiagonal: rowIndex === colIndex,
          })),
      }));
  };

  const toggleScenarioBaseDirection = () => {
    setScenarioBaseDirection((prev) => (prev === "row" ? "column" : "row"));
  };

  const matrixData = generateMatrixCells();

  return (
    <Box className={classes.root}>
      <>
        {fetchingDataForScenarios.length > 0 ? (
          <div className={classes.spinnerContainer}>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className={classes.toggleContainer}>
              <Tooltip
                title={
                  <>
                    <div className={classes.tooltipToggleInfo}>
                      <div>
                        <strong>Change the direction of the matrix</strong>
                      </div>
                      <br />
                      <div>
                        Switch off: Rows are the base scenarios. You should
                        compare them against the columns.
                      </div>
                      <br />
                      <div>
                        Switch on: Columns are the base scenarios. You should
                        compare them against the rows.
                      </div>
                    </div>
                  </>
                }
                style={{ cursor: "default" }}
              >
                <div>Switch Direction</div>
              </Tooltip>
              <Switch
                checked={scenarioBaseDirection === "column"}
                onChange={toggleScenarioBaseDirection}
                color="primary"
                name="cannibalize"
                inputProps={{ "aria-label": "cannibalization-switch" }}
              />
            </div>
            {/* Header columns */}
            <Box className={classes.columnHeader}>
              <Box className={classes.emptySquare} />
              {Array(5)
                .fill(null)
                .map((_, index) => (
                  <Tooltip
                    title={
                      <div
                        className={classes.headerTooltip}
                      >{`${totalsByScenarioId[
                        matrixData[index].cells.find(
                          (cell) => cell.scenarioBaseId !== undefined
                        )?.scenarioBaseId
                      ]?.scenario?.name || "Empty"}`}</div>
                    }
                  >
                    <div
                      key={`header-${index}`}
                      className={classes.columnHeaderCell}
                    >
                      Sc. {index + 1}
                    </div>
                  </Tooltip>
                ))}
            </Box>
            {/* Matrix rows */}
            <Box className={classes.rowHeader}>
              {matrixData.map((row, rowIndex) => (
                <React.Fragment key={`row-${rowIndex}`}>
                  {/* Header row */}
                  <Tooltip
                    title={
                      <div
                        className={classes.headerTooltip}
                      >{`${totalsByScenarioId[
                        matrixData[rowIndex].cells.find(
                          (cell) => cell.scenarioBaseId !== undefined
                        )?.scenarioBaseId
                      ]?.scenario?.name || "Empty"}`}</div>
                    }
                  >
                    <div
                      className={classes.rowHeaderCell}
                      style={{ justifySelf: "end" }}
                    >
                      {row.scenarioName}
                    </div>
                  </Tooltip>
                  {/* Cells */}
                  <>
                    {row.cells.map((cellData, colIndex) => (
                      <ScenarioMatrixCell
                        key={`cell-${rowIndex}-${colIndex}`}
                        cellId={`${rowIndex}-${colIndex}`}
                        cellData={cellData}
                        selectedScenariosData={selectedScenariosData}
                        referenceScenarioId={selectedScenarios[0]}
                        scenarioBaseDirection={scenarioBaseDirection}
                        scenarioBaseName={`${totalsByScenarioId[
                          cellData.scenarioBaseId
                        ]?.scenario?.name || ""}`}
                        scenarioToCompareName={`${totalsByScenarioId[
                          cellData.scenarioToCompareId
                        ]?.scenario?.name || ""}`}
                      />
                    ))}
                  </>
                </React.Fragment>
              ))}
            </Box>
          </>
        )}
      </>
    </Box>
  );
};

export default ScenarioMatrix;
